import React, { useRef, useEffect } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import * as topojson from 'topojson-client';
import './globe-map.css';

const WORLD_ATLAS_URL = 'https://unpkg.com/world-atlas@2.0.2/countries-50m.json';

interface GlobeMapProps {
  usStatus?: 'ONLINE' | 'OFFLINE';
  ukStatus?: 'ONLINE' | 'OFFLINE';
}

const DATA_POINTS = [
  { name: 'US-CENTRAL', key: 'us' as const, coords: [-93.0977, 41.6005] as [number, number] },
  { name: 'EU-LONDON', key: 'uk' as const, coords: [-0.1276, 51.5074] as [number, number] },
];

const NS = 'http://www.w3.org/2000/svg';

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {}
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

export function GlobeMap({
  usStatus = 'ONLINE',
  ukStatus = 'ONLINE',
}: GlobeMapProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const statusMap: Record<string, 'ONLINE' | 'OFFLINE'> = { us: usStatus, uk: ukStatus };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const container = svg.parentElement;
    if (!container) return;

    const width = container.clientWidth || 1000;
    const height = container.clientHeight || 250;

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const projection = geoMercator()
      .scale(width / 6.5)
      .translate([width / 2, height / 1.5]);

    const pathGen = geoPath().projection(projection);

    let cancelled = false;

    fetch(WORLD_ATLAS_URL)
      .then((res) => res.json())
      .then((world) => {
        if (cancelled) return;

        /* eslint-disable @typescript-eslint/no-explicit-any */
        const geo = topojson.feature(world as any, (world as any).objects.countries) as any;
        /* eslint-enable @typescript-eslint/no-explicit-any */

        // Clear previous content
        while (svg.firstChild) svg.removeChild(svg.firstChild);

        // ── Map layer ──
        const gMap = svgEl('g', { class: 'mc-globe__map-layer' });
        svg.appendChild(gMap);

        for (const f of geo.features) {
          const d = pathGen(f);
          if (d) {
            gMap.appendChild(svgEl('path', { class: 'mc-globe__country', d }));
          }
        }

        // ── Nodes layer ──
        const gNodes = svgEl('g', { class: 'mc-globe__nodes-layer' });
        svg.appendChild(gNodes);

        // Connection line
        const p0 = projection(DATA_POINTS[0]!.coords);
        const p1 = projection(DATA_POINTS[1]!.coords);
        if (p0 && p1) {
          gNodes.appendChild(
            svgEl('line', {
              class: 'mc-globe__connection',
              x1: String(p0[0]),
              y1: String(p0[1]),
              x2: String(p1[0]),
              y2: String(p1[1]),
            })
          );
        }

        // Markers
        for (const dp of DATA_POINTS) {
          const projected = projection(dp.coords);
          if (!projected) continue;
          const [x, y] = projected;
          const isOffline = statusMap[dp.key] === 'OFFLINE';
          const modifier = isOffline ? ' mc-globe__marker--offline' : '';

          const g = svgEl('g', {
            class: `mc-globe__marker${modifier}`,
            transform: `translate(${x}, ${y})`,
          });

          g.appendChild(
            svgEl('circle', {
              class: `mc-globe__marker-pulse${isOffline ? ' mc-globe__marker-pulse--offline' : ''}`,
              r: '15',
              cx: '0',
              cy: '0',
            })
          );

          g.appendChild(
            svgEl('circle', {
              class: `mc-globe__marker-core${isOffline ? ' mc-globe__marker-core--offline' : ''}`,
              r: '4',
              cx: '0',
              cy: '0',
            })
          );

          const label = svgEl('text', {
            class: 'mc-globe__marker-label',
            x: '0',
            y: '25',
            'text-anchor': 'middle',
          });
          label.textContent = dp.name;
          g.appendChild(label);

          gNodes.appendChild(g);
        }
      })
      .catch((err) => console.error('Error loading map data:', err));

    return () => {
      cancelled = true;
    };
  }, [usStatus, ukStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mc-globe">
      <svg ref={svgRef} className="mc-globe__svg" preserveAspectRatio="xMidYMid meet" />
    </div>
  );
}
